import { useState } from 'react';
import { Role } from '../types/Role';
import { EditArticleModal } from './EditArticleModal';
import type { Article } from '../types/Article';
import './ArticleRow.css';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/apiClient';
import { toast } from 'react-hot-toast';
import handleShare from './ArticleShare';
import { isMobileDevice } from '../utils/deviceDetection';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

interface ArticleRowProps {
  article: Article;
  userRole?: Role;
  onDelete: (id: number) => Promise<void>;
  onUpdate: (article: Article) => Promise<void>;
}

export default function ArticleRow({ article, userRole, onDelete, onUpdate }: ArticleRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [thumbnailError, _setThumbnailError] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const { isAuthenticated } = useAuth();
  
  const [localArticle, setLocalArticle] = useState<Article>({
    ...article,
    createdAt: article.createdAt ? new Date(article.createdAt) : new Date(),
    updatedAt: article.updatedAt ? new Date(article.updatedAt) : new Date(),
    publishedDate: article.publishedDate || '',
    userId: article.userId || 0
  });

  const isAdmin = userRole === 'ADMIN';

  const isIOSWebView = () => {
    const userAgent = navigator.userAgent.toLowerCase();
    return /iphone|ipad|ipod/.test(userAgent) && /w2n\/ios/.test(userAgent);
  };

  const handleDownload = async () => {
    try {
      if (!isAuthenticated) {
        throw new Error('Usuário não autenticado');
      }

      if (isIOSWebView()) {
        console.log('📱 iOS: Usando visualização para download');
        const token = localStorage.getItem('auth_token');
        const fullPdfUrl = article.pdfUrl?.startsWith('http') 
          ? `${article.pdfUrl}?token=${token}`
          : `${API_URL}${article.pdfUrl}?token=${token}`;

        window.location.href = fullPdfUrl;
      } else if (isMobileDevice()) {
        const token = localStorage.getItem('auth_token');
        const fullPdfUrl = article.pdfUrl?.startsWith('http') 
          ? `${article.pdfUrl}?token=${token}`
          : `${API_URL}${article.pdfUrl}?token=${token}`;
        
        window.location.href = fullPdfUrl;
      } else {
        console.log('💻 Download para desktop');
        await apiClient.downloadPDF(article.id);
      }
    } catch (error) {
      console.error('❌ Erro geral:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao baixar PDF');
    }
  };

  const handleView = async () => {
    if (!isAuthenticated) {
      throw new Error('Usuário não autenticado');
    }

    if (isIOSWebView()) {
      console.log('📱 Visualização no WebView iOS');
      const token = localStorage.getItem('auth_token');
      const fullPdfUrl = article.pdfUrl?.startsWith('http') 
        ? `${article.pdfUrl}?token=${token}`
        : `${API_URL}${article.pdfUrl}?token=${token}`;

      window.location.href = fullPdfUrl;
    } else if (isMobileDevice()) {
      const token = localStorage.getItem('auth_token');
      const fullPdfUrl = article.pdfUrl?.startsWith('http') 
        ? `${article.pdfUrl}?token=${token}`
        : `${API_URL}${article.pdfUrl}?token=${token}`;
      
      window.location.href = fullPdfUrl;
    } else {
      const fullPdfUrl = article.pdfUrl?.startsWith('http') 
        ? article.pdfUrl 
        : `${API_URL}${article.pdfUrl}`;
      
      window.open(fullPdfUrl, '_blank');
    }
  };

  const onShareClick = () => {
    handleShare(localArticle);
  };

  const handleSave = async (updatedArticle: Article) => {
    try {
      const updatedLocalArticle = {
        ...updatedArticle,
        createdAt: new Date(updatedArticle.createdAt),
        updatedAt: new Date(updatedArticle.updatedAt),
        publishedDate: updatedArticle.publishedDate
      };
      
      setLocalArticle(updatedLocalArticle);
      await onUpdate(updatedArticle);
      setIsEditModalOpen(false);
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error('Erro ao salvar as alterações');
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete(article.id);
    } catch (error) {
      console.error('Erro ao excluir:', error);
      toast.error('Erro ao excluir o artigo');
    }
  };

  return (
    <>
      <div className="article-row">
        <div className="article-header" onClick={() => setIsExpanded(!isExpanded)}>
          <h3>{localArticle.title}</h3>
          <span className={`expand-icon ${isExpanded ? 'expanded' : ''}`}>▼</span>
        </div>
        
        {isExpanded && (
          <div className="article-details">
            <div className="article-info">
              <div className="info-column">
                <div className="info-item">
                  <strong>DOI</strong>
                  <p>{localArticle.doi || 'Não informado'}</p>
                </div>
                <div className="info-item">
                  <strong>Revista</strong>
                  <p>{localArticle.journal || 'Não informado'}</p>
                </div>
                <div className="info-item">
                  <strong>Data de Publicação</strong>
                  <p>{localArticle.publishedDate || 'Não informado'}</p>
                </div>
                <div className="info-item">
                  <strong>Palavras-chave</strong>
                  <div className="keywords">
                    {localArticle.keywords.length > 0 ? (
                      localArticle.keywords.map((keyword: string, index: number) => (
                        <span key={index} className="keyword">{keyword}</span>
                      ))
                    ) : (
                      <span className="keyword">Nenhuma palavra-chave</span>
                    )}
                  </div>
                </div>
              </div>
              {localArticle.coverUrl && !thumbnailError && (
                <div className="thumbnail-column">
                  <img 
                    src={localArticle.coverUrl} 
                    alt="Capa do artigo" 
                    className="article-thumbnail"
                  />
                  {isAdmin && (
                    <span 
                      className="edit-thumbnail-text"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsEditModalOpen(true);
                      }}
                    >
                      Editar artigo
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="article-actions">
              <button
                onClick={handleDownload}
                className="action-button download"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 16l-5-5h3V4h4v7h3l-5 5zm-5 4h10v-2H7v2z"/>
                </svg>
                Download
              </button>

              <button
                onClick={handleView}
                className="action-button visualize"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                </svg>
                Visualizar
              </button>

              <button
                onClick={onShareClick}
                className="action-button share"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/>
                </svg>
                Compartilhar
              </button>
            </div>
          </div>
        )}
      </div>

      <EditArticleModal
        isOpen={isEditModalOpen}
        article={localArticle}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSave}
        onDelete={handleDelete}
      />
    </>
  );
}

declare global {
  interface Window {
    webkit?: {
      messageHandlers?: {
        downloadPDF?: {
          postMessage: (message: any) => void;
        };
        viewPDF?: {
          postMessage: (message: any) => void;
        };
      };
    };
  }
} 